import { useState } from 'react';
import { useIntl } from 'react-intl';

import { EntityVisibilitySelect } from '~/components/Shared/EntityVisibilitySelect/EntityVisibilitySelect';
import { validateShareField } from '~/components/Shared/EntityVisibilitySelect/utils';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Form } from '~/eds/src/modules/Form/Form';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { Workflow } from '~/types/Workflow';

type Visibility = 'OPEN' | 'PRIVATE' | 'SHARED';

type InitialStateType = {
  shareField: {
    visibility: Visibility;
    userIds: number[];
    departmentIds: number[];
  };
};

interface ShareWorkflowModalProps {
  workflow: Nullable<Workflow>;
  onHide?: () => void;
}

const fields = {
  shareField: {
    order: 1,
    key: 'shareField',
    type: 'field' as const,
    name: 'shareField',
    label: 'Visibility',
    description: 'Who can see and use this workflow template.',
    input: EntityVisibilitySelect,
    inputProps: {
      enablePortal: false,
    },
  },
};

export const useShareWorkflowModal = ({
  workflow,
  onHide,
}: ShareWorkflowModalProps) => {
  const currentUser = useCurrentUser();
  const { toast } = useToast();
  const intl = useIntl();
  const [isValid, setIsValid] = useState(true);
  const [currentState, setCurrentState] = useState<{
    visibility: Visibility;
    userIds: number[];
    departmentIds: number[];
  }>({
    visibility: 'OPEN',
    userIds: [],
    departmentIds: [],
  });

  const workflowId = workflow?.id;

  const { data, isFetching } = api.endpoints.getWorkflowShareSettings.useQuery(
    {
      workflowId: workflowId!,
      currentUserId: currentUser.id,
    },
    {
      skip: !workflowId || !currentUser,
    },
  );

  const [updateWorkflowShareSettings, { isLoading: isUpdating }] =
    api.endpoints.updateWorkflowShareSettings.useMutation();

  const initialState = {
    shareField: {
      value: data
        ? {
            visibility: data.visibility as Visibility,
            userIds: data.userIds,
            departmentIds: data.departmentIds,
          }
        : currentState,
    },
  };

  const getSuccessMessage = () => {
    let sharedWith = intl.formatMessage({
      id: 'UseShareWorkflowModal.sharedWithPrefix',
      defaultMessage: 'Shared with',
      description: 'Prefix for success message about sharing',
    });

    if (currentState.departmentIds.length > 0) {
      sharedWith += ` ${intl.formatMessage(
        {
          id: 'UseShareWorkflowModal.departmentsCount',
          defaultMessage:
            '{count, plural, one {# department} other {# departments}}',
          description:
            'Count of departments shared with. count is the number of departments',
        },
        { count: currentState.departmentIds.length },
      )}`;
    }
    if (
      currentState.departmentIds.length > 0 &&
      currentState.userIds.length > 0
    ) {
      sharedWith += ` ${intl.formatMessage({
        id: 'UseShareWorkflowModal.andConnector',
        defaultMessage: 'and',
        description: 'Connector word between departments and people',
      })}`;
    }
    if (currentState.userIds.length > 0) {
      sharedWith += ` ${intl.formatMessage(
        {
          id: 'UseShareWorkflowModal.peopleCount',
          defaultMessage: '{count, plural, one {# person} other {# people}}',
          description:
            'Count of people shared with. count is the number of people',
        },
        { count: currentState.userIds.length },
      )}`;
    }
    if (
      currentState.departmentIds.length === 0 &&
      currentState.userIds.length === 0
    ) {
      sharedWith += ` ${intl.formatMessage(
        {
          id: 'UseShareWorkflowModal.everyoneAtClient',
          defaultMessage: 'everyone at {clientName}',
          description:
            'Message when shared with everyone at client. clientName is the name of the client organization Example: clientName = "Acme Corp", "ABC Industries"',
        },
        { clientName: currentUser.client_name },
      )}`;
    }
    return sharedWith;
  };

  const handleUpdateShareSettings = async () => {
    try {
      await updateWorkflowShareSettings({
        workflowId: workflowId!,
        shareSettings: {
          userIds: currentState.userIds,
          departmentIds: currentState.departmentIds,
        },
        hasSettings: data!.hasSettings,
        currentUserId: currentUser.id,
      }).unwrap();
      hide();
      toast({
        status: 'success',
        message: getSuccessMessage(),
      });
    } catch (e: any) {
      if (e?.response?.status === 403) {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'UseShareWorkflowModal.noPermissionError',
            defaultMessage:
              'You do not have permission to update this workflow share settings.',
            description:
              'Error message when user lacks permission to update share settings',
          }),
        });
      } else {
        toast({
          status: 'danger',
          message: intl.formatMessage({
            id: 'UseShareWorkflowModal.updateFailedError',
            defaultMessage: 'Failed to update this workflow share settings.',
            description:
              'Generic error message when updating share settings fails',
          }),
        });
      }
    }
  };

  const [modal, show, hide] = useModal({
    title: intl.formatMessage(
      {
        id: 'UseShareWorkflowModal.modalTitle',
        defaultMessage: 'Share "{workflowName}"',
        description:
          'Modal title for sharing workflow. workflowName is the name of the workflow being shared Example: workflowName = "Customer Onboarding", "Contract Review")',
      },
      { workflowName: workflow?.name },
    ),
    onHide,
    primaryAction: {
      onClick: handleUpdateShareSettings,
      text: intl.formatMessage({
        id: 'UseShareWorkflowModal.saveButton',
        defaultMessage: 'Save',
        description: 'Button text for saving share settings',
      }),
      isLoading: isUpdating || isFetching,
      disabled: !isValid,
      tooltip: !isValid
        ? intl.formatMessage({
            id: 'UseShareWorkflowModal.fixErrorsTooltip',
            defaultMessage: 'Please fix the errors before submitting',
            description: 'Tooltip shown when form has validation errors',
          })
        : undefined,
    },
    children: (
      <ContentContainer
        loadingContent={{
          isLoading: isFetching,
          message: intl.formatMessage({
            id: 'UseShareWorkflowModal.loadingMessage',
            defaultMessage: 'Loading share settings...',
            description: 'Loading message while fetching share settings',
          }),
        }}
      >
        <Form<InitialStateType>
          fields={fields}
          onChange={(updatedState) => {
            const visibility = updatedState.state.shareField.value.visibility;
            const userIds =
              visibility === 'PRIVATE'
                ? [currentUser.id]
                : visibility === 'OPEN'
                  ? []
                  : updatedState.state.shareField.value.userIds;
            setCurrentState({
              visibility,
              userIds,
              departmentIds:
                updatedState.state.shareField.value.departmentIds ?? [],
            });
          }}
          state={initialState}
          validators={[validateShareField]}
          onValidate={(updatedState) => {
            setIsValid(Object.keys(updatedState.errors ?? {}).length === 0);
          }}
        />
      </ContentContainer>
    ),
  });

  return [modal, show, hide] as const;
};
